import type { Extensions, JSONContent } from "@tiptap/core"
import type { Mark, Node } from "@tiptap/pm/model"
import type { TiptapStaticRendererOptions } from "@tiptap/static-renderer"
import {
  renderToHTMLString,
  serializeChildrenToHTMLString,
} from "@tiptap/static-renderer/pm/html-string"
import { renderToMarkdown as upstreamRenderToMarkdown } from "@tiptap/static-renderer/pm/markdown"

export function renderToText({
  doc,
  extensions,
  options,
}: {
  doc: JSONContent
  extensions: Extensions
  options?: Partial<TiptapStaticRendererOptions<string, Mark, Node>>
}) {
  return renderToHTMLString({
    content: doc,
    extensions,
    options: {
      nodeMapping: {
        bulletList({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        orderedList({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        listItem({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        paragraph({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        heading({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        codeBlock() {
          return ""
        },
        blockquote({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        callout({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        image({ node }) {
          return node.attrs.alt
        },
        snippet({ node }) {
          return node.attrs.title ?? ""
        },
        subscribe() {
          return ""
        },
        embed() {
          return ""
        },
        math() {
          return ""
        },
        mathInline() {
          return ""
        },
        hardBreak() {
          return " "
        },
        horizontalRule() {
          return ""
        },
        table({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        tableRow({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        tableHeader({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        tableCell({ children }) {
          return `${serializeChildrenToHTMLString(children)} `
        },
        ...options?.nodeMapping,
      },
      markMapping: {
        bold({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        italic({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        code({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        strike({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        underline({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        subscript({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        superscript({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        link({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        highlight({ children }) {
          return serializeChildrenToHTMLString(children)
        },
        ...options?.markMapping,
      },
      ...options,
    },
  })
}

export function renderToMarkdown({
  content,
  extensions,
  options,
}: {
  content: Node | JSONContent
  extensions: Extensions
  options?: Partial<TiptapStaticRendererOptions<string, Mark, Node>>
}) {
  return upstreamRenderToMarkdown({
    content,
    extensions,
    options: {
      ...options,
      nodeMapping: {
        math({ node }) {
          return `\n$$\n${node.attrs.latex}\n$$\n`
        },
        mathInline({ node }) {
          return `$${node.attrs.latex}$`
        },
        embed({ node }) {
          const url = node.attrs.url || node.attrs.embedUrl
          const title = node.attrs.title
          if (title && url) {
            return `\n[${title}](${url})\n`
          }
          if (url) {
            return `\n${url}\n`
          }
          return ""
        },
        table({ children, node }) {
          if (!Array.isArray(children)) {
            return `\n${serializeChildrenToHTMLString(children)}\n`
          }

          const columnCount = node.children[0]?.childCount
          return `\n${serializeChildrenToHTMLString(children[0])}| ${new Array(columnCount).fill("---").join(" | ")} |\n${serializeChildrenToHTMLString(children.slice(1))}\n`
        },
        ...options?.nodeMapping,
      },
      markMapping: {
        underline({ children }) {
          return `<u>${serializeChildrenToHTMLString(children)}</u>`
        },
        subscript({ children }) {
          return `<sub>${serializeChildrenToHTMLString(children)}</sub>`
        },
        superscript({ children }) {
          return `<sup>${serializeChildrenToHTMLString(children)}</sup>`
        },
        ...options?.markMapping,
      },
    },
  })
}
